// frontend/src/pages/OvertimeMonitoring/components/OvertimeAlerts.js
import React, { useMemo } from 'react';
import {
  AlertTriangle,
  AlertOctagon,
  XCircle,
  Eye,
  TrendingUp,
  CalendarClock,
  CheckCircle,
  ChevronRight,
  BookOpen,
} from 'lucide-react';
import { CountBadge } from '../../../components/UI/CountBadge';
import { StatusBadge } from '../../../components/UI/StatusBadge';
import { DepartmentTradeChips } from '../../../components/UI/DepartmentTradeChips';
import { formatHoursMinutes } from '../../../utils/dateUtils';

/**
 * OvertimeAlerts — HR action inbox for 72h OT risk.
 *
 * Sourced from the same live employees snapshot as Employee Tracking
 * (not the legacy OvertimeComplianceAlert table). Digests notify
 * managers; this tab answers "who needs my attention this month?"
 *
 * Sections:
 *   1. Needs action — exceeded / critical / projected_exceed / at_risk / watch
 *   2. Potential undeclared OT — late clock-out with no OT declaration
 *   3. Status legend — badges HR sees on this page (aligned with Tracking)
 */

const ACTION_STATUSES = new Set([
  'exceeded',
  'critical',
  'projected_exceed',
  'at_risk',
  'watch',
]);

const STATUS_PRIORITY = {
  exceeded: 6,
  critical: 5,
  projected_exceed: 4,
  at_risk: 3,
  watch: 2,
};

const STATUS_CHROME = {
  exceeded: {
    icon: XCircle,
    borderColor: 'border-l-red-500',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
  },
  critical: {
    icon: AlertOctagon,
    borderColor: 'border-l-red-400',
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
  },
  projected_exceed: {
    icon: TrendingUp,
    borderColor: 'border-l-orange-500',
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
  },
  at_risk: {
    icon: AlertTriangle,
    borderColor: 'border-l-orange-400',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
  },
  watch: {
    icon: Eye,
    borderColor: 'border-l-amber-400',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
  },
};

const LEGEND_ITEMS = [
  {
    status: 'watch',
    title: 'Watch',
    detail: 'On pace for >60h by month end — plan ahead with the RO.',
  },
  {
    status: 'at_risk',
    title: 'At Risk',
    detail: 'Current cap-eligible OT is above 48h.',
  },
  {
    status: 'projected_exceed',
    title: 'Projected Exceed',
    detail: 'Trajectory will breach the 72h MOM cap before month end.',
  },
  {
    status: 'critical',
    title: 'Critical',
    detail: 'Current cap-eligible OT is above 60h — act now.',
  },
  {
    status: 'exceeded',
    title: 'Exceeded',
    detail: 'At or over the 72h MOM monthly cap — compliance incident.',
  },
];

const getEmployeeStatus = (employee) =>
  employee?.unifiedStatus || employee?.riskLevel || 'compliant';

const statusSummaryLine = (employee) => {
  const status = getEmployeeStatus(employee);
  const hours = formatHoursMinutes(employee.currentOtHours);
  const forecast = employee.forecast;
  const projected =
    forecast?.projectedEomOt != null
      ? formatHoursMinutes(forecast.projectedEomOt)
      : null;

  if (status === 'exceeded') {
    return `${hours} OT this month — exceeded the 72h MOM cap`;
  }
  if (status === 'critical') {
    return `${hours} OT this month — above 60h (critical)`;
  }
  if (status === 'at_risk') {
    return `${hours} OT this month — above 48h (at risk)`;
  }
  if (status === 'projected_exceed') {
    return projected
      ? `${hours} so far · projected ${projected} by month end`
      : `${hours} so far · projected to exceed 72h`;
  }
  if (status === 'watch') {
    return projected
      ? `${hours} so far · projected ${projected} by month end`
      : `${hours} so far · on the forecast watch list`;
  }
  return `${hours} OT this month`;
};

const OvertimeAlerts = ({
  employees,
  loading,
  isCurrentMonth = true,
  onViewEmployee,
}) => {
  const { actionEmployees, undeclaredEmployees } = useMemo(() => {
    const list = employees || [];
    const action = list
      .filter((e) => ACTION_STATUSES.has(getEmployeeStatus(e)))
      .sort((a, b) => {
        const pa = STATUS_PRIORITY[getEmployeeStatus(a)] || 0;
        const pb = STATUS_PRIORITY[getEmployeeStatus(b)] || 0;
        if (pb !== pa) return pb - pa;
        return (Number(b.currentOtHours) || 0) - (Number(a.currentOtHours) || 0);
      });
    const undeclared = list
      .filter((e) => e.hasUndeclaredOt)
      .sort(
        (a, b) => (Number(b.currentOtHours) || 0) - (Number(a.currentOtHours) || 0)
      );
    return { actionEmployees: action, undeclaredEmployees: undeclared };
  }, [employees]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <div className="h-6 bg-gray-200 rounded w-48 mb-4 animate-pulse" />
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="p-4 bg-gray-50 rounded-xl animate-pulse">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-gray-200 rounded-full mr-4" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/3" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Needs action — live snapshot */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2 text-red-600 flex-shrink-0" />
                Needs Action
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Live roster for this month — same data as Employee Tracking.
                Manager digests are audited under Notification Coverage.
              </p>
            </div>
            <div className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 flex-shrink-0">
              <CountBadge count={actionEmployees.length} theme="admin" size="md" />
              <span>{actionEmployees.length === 1 ? 'worker' : 'workers'}</span>
            </div>
          </div>
        </div>

        {actionEmployees.length === 0 ? (
          <div className="text-center py-12 px-4">
            <CheckCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No workers need OT action
            </h3>
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              Nobody is at risk, critical, exceeded, or on the forecast watch
              list for this month.
              {undeclaredEmployees.length > 0
                ? ' Check Potential Undeclared Overtime below for declaration gaps.'
                : ''}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {actionEmployees.map((employee) => {
              const status = getEmployeeStatus(employee);
              const chrome = STATUS_CHROME[status] || STATUS_CHROME.at_risk;
              const Icon = chrome.icon;
              const forecast = employee.forecast;
              const showForecastMeta =
                isCurrentMonth &&
                forecast &&
                (status === 'projected_exceed' || status === 'watch');

              return (
                <li key={employee.staffId}>
                  <button
                    type="button"
                    onClick={() => onViewEmployee?.(employee)}
                    className={`w-full text-left p-4 bg-white border-l-4 ${chrome.borderColor} hover:bg-gray-50 active:bg-gray-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-red-500`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start min-w-0">
                        <div
                          className={`w-10 h-10 ${chrome.iconBg} rounded-full flex items-center justify-center flex-shrink-0`}
                        >
                          <Icon className={`w-5 h-5 ${chrome.iconColor}`} />
                        </div>
                        <div className="ml-4 min-w-0">
                          <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mb-1">
                            <h4 className="text-sm font-medium text-gray-900 truncate">
                              {employee.fullName}
                              <span className="ml-2 text-xs font-normal text-gray-500">
                                · {employee.staffId}
                              </span>
                            </h4>
                            <StatusBadge status={status} />
                            {employee.hasUndeclaredOt && (
                              <span
                                className="inline-flex items-center text-orange-600"
                                title="Potential undeclared overtime"
                              >
                                <AlertTriangle className="w-3.5 h-3.5" />
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">
                            {statusSummaryLine(employee)}
                          </p>
                          <div className="flex items-center flex-wrap gap-1.5 mt-2">
                            <DepartmentTradeChips
                              department={employee.department}
                              trade={employee.trade}
                              layout="inline"
                            />
                            {showForecastMeta && forecast.projectedBreachDate && (
                              <>
                                <span className="inline-flex items-center gap-0.5 text-red-600 font-medium">
                                  <CalendarClock className="w-3 h-3 flex-shrink-0" />
                                  Cap{' '}
                                  {new Date(
                                    `${forecast.projectedBreachDate}T00:00:00`
                                  ).toLocaleDateString('en-SG', {
                                    day: 'numeric',
                                    month: 'short',
                                  })}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 self-center">
                        <span className="text-sm font-semibold text-gray-900 tabular-nums">
                          {formatHoursMinutes(employee.currentOtHours)}
                        </span>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Potential undeclared OT */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2 text-orange-600 flex-shrink-0" />
                Potential Undeclared Overtime
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                {isCurrentMonth
                  ? 'Clock-out after 17:30 SGT on a working day with no OT declared for that date. Verify with the RO before acting.'
                  : 'Undeclared-OT detection runs for the current month only.'}
              </p>
            </div>
            <div className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 flex-shrink-0">
              <CountBadge count={undeclaredEmployees.length} theme="warning" size="md" />
              <span>{undeclaredEmployees.length === 1 ? 'employee' : 'employees'}</span>
            </div>
          </div>
        </div>

        {!isCurrentMonth ? (
          <div className="text-center py-12 px-4">
            <Eye className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Not available for past months
            </h3>
            <p className="text-sm text-gray-500">
              Switch to the current month to review undeclared overtime signals.
            </p>
          </div>
        ) : undeclaredEmployees.length === 0 ? (
          <div className="text-center py-12 px-4">
            <CheckCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No undeclared OT signals
            </h3>
            <p className="text-sm text-gray-500">
              No late clock-outs without an OT declaration this month.
            </p>
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <ul className="md:hidden divide-y divide-gray-100">
              {undeclaredEmployees.map((employee) => {
                const status = getEmployeeStatus(employee);
                return (
                  <li key={employee.staffId}>
                    <button
                      type="button"
                      onClick={() => onViewEmployee?.(employee)}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 active:bg-gray-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-red-500"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {employee.fullName}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">{employee.staffId}</p>
                          <div className="flex items-center flex-wrap gap-1.5 mt-1.5">
                            <DepartmentTradeChips
                              department={employee.department}
                              trade={employee.trade}
                              layout="inline"
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Declared OT:{' '}
                            <span className="font-medium text-gray-700">
                              {formatHoursMinutes(employee.currentOtHours)}
                            </span>
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <StatusBadge status={status} />
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">
                      Employee
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">
                      Department / Trade
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">
                      Declared OT
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-600">
                      <span className="sr-only">Open</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {undeclaredEmployees.map((employee) => {
                    const status = getEmployeeStatus(employee);
                    return (
                      <tr
                        key={employee.staffId}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => onViewEmployee?.(employee)}
                      >
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-gray-900">
                            {employee.fullName}
                          </p>
                          <p className="text-xs text-gray-500">{employee.staffId}</p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center flex-wrap gap-1.5">
                            <DepartmentTradeChips
                              department={employee.department}
                              trade={employee.trade}
                              layout="inline"
                            />
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right tabular-nums">
                          {formatHoursMinutes(employee.currentOtHours)}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={status} />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <ChevronRight className="w-4 h-4 text-gray-400 inline-block" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Status legend — matches Tracking badges, not the old 48/60 zone cards */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <BookOpen className="w-5 h-5 mr-2 text-red-600" />
            Status Legend
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Badges match Employee Tracking. Hard cap is 72h/month (MOM Part IV).
            Digests use a finer projection scale (40→72h) for manager emails —
            see Notification Coverage for delivery.
          </p>
        </div>
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {LEGEND_ITEMS.map((item) => (
            <div
              key={item.status}
              className="flex items-start gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50/50"
            >
              <div className="flex-shrink-0 pt-0.5">
                <StatusBadge status={item.status} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">{item.title}</p>
                <p className="text-xs text-gray-500 mt-0.5">{item.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OvertimeAlerts;

/** Shared helper for tab badge counts (unfiltered roster). */
export const countOvertimeActionItems = (employees = []) =>
  employees.filter((e) => ACTION_STATUSES.has(getEmployeeStatus(e))).length;
